import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { X } from 'lucide-react';
import { gameDepartmentAPI } from '../../services/api';
import GameSearchBox from './GameSearchBox';
import useBrandChange from './useBrandChange';
import GameStickinessCurve, { CURVE_METRICS } from './GameStickinessCurve';
import PerformanceTable from './PerformanceTable';
import {
  MAX_COMPARE_GAMES, addGame, removeGame, mergeSeries, periodLabel, rangeForLastDays, toRequestRange, todayUtc,
} from './performanceData';

const AXIS_COLOR = '#7a8499';

const METRICS = [
  { key: 'turnover_eur', label: 'Turnover', fmt: fmtEur, axisFmt: fmtEurCompact },
  { key: 'ggr_eur',      label: 'GGR',      fmt: fmtEur, axisFmt: fmtEurCompact },
  { key: 'actives',      label: 'Actives',  fmt: fmtInt, axisFmt: fmtIntCompact },
  { key: 'n_bets',       label: 'Bets',     fmt: fmtInt, axisFmt: fmtIntCompact },
  // Payout hovers around ~95–100%, so a zero-based axis would flatten it —
  // let the axis fit the data instead.
  { key: 'payout_pct',   label: 'Payout',   fmt: fmtPct, axisFmt: fmtPctAxis, domain: ['auto', 'auto'] },
];

const BUCKETS = [
  { key: 'day',   label: 'Daily' },
  { key: 'week',  label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

const DATE_RANGE_PRESETS = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m',  days: 182 },
  { label: '1y',  days: 365 },
];

const VIEWS = [
  { key: 'over_time',  label: 'Over time' },
  { key: 'stickiness', label: 'Stickiness curve' },
];

function fmtInt(v) { return Number(v).toLocaleString('en-GB'); }
function fmtEur(v) { return `€${Number(v).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`; }
// Compact axis ticks (€27.5M vs €27,500,000) — the full-precision formatters
// above are too wide for a fixed-width Y axis and get clipped by the chart's
// left edge (see the clipped-tick incident this was written to fix).
function fmtIntCompact(v) { return Number(v).toLocaleString('en-GB', { notation: 'compact', maximumFractionDigits: 1 }); }
function fmtEurCompact(v) { return `€${fmtIntCompact(v)}`; }
function fmtPct(v) { return v == null ? '—' : `${Number(v).toFixed(2)}%`; }
function fmtPctAxis(v) { return `${Number(v).toFixed(0)}%`; }

function GameChip({ game, color, onClear }) {
  return (
    <div className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <div>
        <p className="text-sm font-bold text-ink">{game.game_title}</p>
        <p className="text-[11px] text-ink-dim">{game.provider}</p>
      </div>
      <button
        type="button"
        onClick={onClear}
        className="ml-1 p-1 rounded-md text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors"
        aria-label={`Remove ${game.game_title}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// Reads values off the raw chart row rather than recharts' payload entries:
// a null value (e.g. payout on a zero-turnover bucket) drops its series from
// the payload, but the row itself still has every game.
function PerformanceTooltip({ active, payload, metricFmt, games }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-surface-2 border border-white/10 rounded-md p-2.5 text-[11px] max-w-[260px]">
      <p className="font-bold text-ink mb-1">{point.label}</p>
      {games.map(g => (
        <div key={g.key} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
          {games.length > 1 && <span className="text-ink-dim truncate">{g.title}</span>}
          <span className="text-ink ml-auto pl-2">{metricFmt(point[g.key])}</span>
        </div>
      ))}
    </div>
  );
}

function OverTimeControls({ bucket, setBucket, range, setRange, metric, setMetric }) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <div>
        <label className="text-[11px] text-ink-dim mb-1 block">Bucket</label>
        <Select value={bucket} onValueChange={setBucket}>
          <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {BUCKETS.map(b => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-[11px] text-ink-dim mb-1 block">From</label>
        <Input
          type="date" value={range.from} max={range.to}
          onChange={e => e.target.value && setRange(r => ({ ...r, from: e.target.value }))}
          className="w-[150px]"
        />
      </div>
      <div>
        <label className="text-[11px] text-ink-dim mb-1 block">To</label>
        <Input
          type="date" value={range.to} min={range.from} max={todayUtc().toISOString().slice(0, 10)}
          onChange={e => e.target.value && setRange(r => ({ ...r, to: e.target.value }))}
          className="w-[150px]"
        />
      </div>
      <div className="flex items-center gap-1">
        {DATE_RANGE_PRESETS.map(p => (
          <Button key={p.days} variant="ghost" size="sm" onClick={() => setRange(rangeForLastDays(p.days))}>
            {p.label}
          </Button>
        ))}
      </div>
      <div>
        <label className="text-[11px] text-ink-dim mb-1 block">Metric</label>
        <Select value={metric} onValueChange={setMetric}>
          <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function OverTimeChart({ chartData, chartGames, activeMetric }) {
  // Filled areas pile up into mud past two games; lines alone stay readable.
  const fillOpacity = chartGames.length === 1 ? 0.35 : chartGames.length === 2 ? 0.2 : 0;
  return (
    <div className="h-[320px] mb-6">
      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-full text-xs text-ink-dim">No data for this range</div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
            <defs>
              {chartGames.map(g => (
                <linearGradient key={g.key} id={`gameMetricFill-${g.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={g.color} stopOpacity={fillOpacity} />
                  <stop offset="100%" stopColor={g.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="label" stroke={AXIS_COLOR} fontSize={11} />
            <YAxis tickFormatter={activeMetric.axisFmt} domain={activeMetric.domain || [0, 'auto']} stroke={AXIS_COLOR} fontSize={11} width={56} />
            <Tooltip content={<PerformanceTooltip metricFmt={activeMetric.fmt} games={chartGames} />} cursor={{ stroke: 'rgba(255,255,255,0.15)' }} />
            {chartGames.map(g => (
              <Area
                key={g.key}
                type="monotone"
                dataKey={g.key}
                name={g.title}
                stroke={g.color}
                strokeWidth={2}
                fill={`url(#gameMetricFill-${g.key})`}
                dot={chartGames.length > 2 ? false : { r: 2 }}
                activeDot={{ r: 4 }}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default function GamePerformanceReport() {
  // A game clicked in another report (e.g. Game Metrics) arrives via router
  // state, so this view opens straight into its performance instead of
  // making the user search for it again.
  const location = useLocation();
  // Several games (ticked in the Stickiness report) arrive as state.games.
  const [selected, setSelected] = useState(() => {
    const incoming = location.state?.games || (location.state?.game ? [location.state.game] : []);
    return incoming.reduce(addGame, []);
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [bucket, setBucket] = useState('day');
  // The Releases report sends a range opening on the games' release dates.
  const [range, setRange] = useState(() => location.state?.range || rangeForLastDays(30));
  const [metric, setMetric] = useState(METRICS[0].key);
  const [view, setView] = useState(() => (VIEWS.some(v => v.key === location.state?.view) ? location.state.view : VIEWS[0].key));
  const [curveMetric, setCurveMetric] = useState(CURVE_METRICS[0].key);
  // unique_game_id is per brand: the picked games don't exist in the new one.
  useBrandChange(() => setSelected([]));

  const chartGames = useMemo(() => selected.map(s => ({
    key: `g${s.game.unique_game_id}`,
    title: s.game.game_title,
    uniqueGameId: s.game.unique_game_id,
    color: s.color,
  })), [selected]);

  const selectionIds = chartGames.map(g => g.key).join(',');

  useEffect(() => {
    if (chartGames.length === 0) { setRows([]); return undefined; }
    if (view !== 'over_time') return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    const req = { bucket, ...toRequestRange(range) };
    Promise.all(chartGames.map(g => gameDepartmentAPI
      .getGamePerformance({ uniqueGameId: g.uniqueGameId, ...req })
      .then(res => [g.key, res.data?.points || []])))
      .then(entries => { if (!cancelled) setRows(mergeSeries(Object.fromEntries(entries))); })
      .catch(() => { if (!cancelled) setError('Failed to load game performance.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // selectionIds captures chartGames' identity; the array is rebuilt on every selection change anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionIds, bucket, range, view]);

  const activeMetric = METRICS.find(m => m.key === metric);

  const chartData = useMemo(() => rows.map(r => ({
    period_start: r.period_start,
    label: periodLabel(r.period_start, bucket),
    ...Object.fromEntries(chartGames.map(g => [g.key, r.values[g.key]?.[metric] ?? null])),
  })), [rows, chartGames, bucket, metric]);

  const hasGames = selected.length > 0;
  const canAddMore = hasGames && selected.length < MAX_COMPARE_GAMES;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Game Performance</CardTitle>
        <CardDescription>
          Search for a game to see its turnover, GGR, active players, bet counts and realized payout broken down
          by day, week or month, or switch to the stickiness curve to see how quickly players&apos; spend on it tails
          off after their first bet. Add up to {MAX_COMPARE_GAMES} games to compare them side by side.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          {selected.map(s => (
            <GameChip key={s.game.unique_game_id} game={s.game} color={s.color} onClear={() => setSelected(sel => removeGame(sel, s.game.unique_game_id))} />
          ))}
          {!hasGames && <GameSearchBox onSelect={g => setSelected(sel => addGame(sel, g))} />}
          {canAddMore && (
            <div className="w-[280px]">
              <GameSearchBox
                onSelect={g => setSelected(sel => addGame(sel, g))}
                placeholder={`Compare with another game… (${selected.length}/${MAX_COMPARE_GAMES})`}
              />
            </div>
          )}
          {hasGames && !canAddMore && (
            <span className="text-[11px] text-ink-dim">Up to {MAX_COMPARE_GAMES} games — remove one to add another.</span>
          )}
        </div>

        {!hasGames ? (
          <div className="flex items-center justify-center py-10 text-xs text-ink-dim">
            Search for a game above to see its performance over time.
          </div>
        ) : (
          <>
            <ToggleGroup value={view} onChange={setView} className="mb-4">
              {VIEWS.map(v => <ToggleItem key={v.key} value={v.key}>{v.label}</ToggleItem>)}
            </ToggleGroup>

            {view === 'stickiness' ? (
              <>
                <div className="flex flex-wrap items-end gap-3 mb-4">
                  <div>
                    <label className="text-[11px] text-ink-dim mb-1 block">Metric</label>
                    <Select value={curveMetric} onValueChange={setCurveMetric}>
                      <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CURVE_METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <GameStickinessCurve games={chartGames} metric={curveMetric} showReturnDays />
              </>
            ) : (
              <>
                <OverTimeControls
                  bucket={bucket} setBucket={setBucket}
                  range={range} setRange={setRange}
                  metric={metric} setMetric={setMetric}
                />
                {loading ? (
                  <div className="flex justify-center py-10"><Spinner size="sm" /></div>
                ) : (
                  <>
                    <OverTimeChart chartData={chartData} chartGames={chartGames} activeMetric={activeMetric} />
                    <PerformanceTable rows={rows} games={chartGames} metrics={METRICS} metric={metric} bucket={bucket} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
